import { useEffect, useState } from "react";
import { MessageSquare, Megaphone, Search as SearchIcon, X } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../../connect/api";
import { Avatar, CEO_ORG, dayLabel, Empty, Header, Spinner } from "./ui";

const Mark = ({ text, q }) => {
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return <>{text}</>;
  return <>{text.slice(0, i)}<mark className="bg-amber-400/30 text-amber-100 rounded px-0.5">{text.slice(i, i + q.length)}</mark>{text.slice(i + q.length)}</>;
};

export const Search = () => {
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("messages");
  const [res, setRes] = useState({ messages: [], posts: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) { setRes({ messages: [], posts: [] }); return; }
    setLoading(true);
    const t = setTimeout(() => api.get("/search", { params: { q: q.trim() } }).then((r) => setRes(r.data)).catch(() => {}).finally(() => setLoading(false)), 300);
    return () => clearTimeout(t);
  }, [q]);

  const list = tab === "messages" ? res.messages : res.posts;
  return (
    <div className="h-full flex flex-col">
      <Header title="Search" sub="Conversations and All Staff announcements" back />
      <div className="px-3 pt-3 pb-2 space-y-2 shrink-0 border-b border-white/5">
        <div className="relative">
          <SearchIcon size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input autoFocus className="nxc-input pl-10 pr-10 py-2.5" placeholder="Search by keyword" value={q} onChange={(e) => setQ(e.target.value)} data-testid="search-input" />
          {q && <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200" data-testid="search-clear"><X size={16} /></button>}
        </div>
        <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-white/[0.04] border border-white/5">
          {[["messages", "Messages", MessageSquare, res.messages.length], ["posts", "All Staff", Megaphone, res.posts.length]].map(([k, label, Icon, n]) => (
            <button key={k} onClick={() => setTab(k)} className={`h-9 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors ${tab === k ? "bg-blue-500/20 text-blue-300" : "text-slate-400 hover:text-slate-200"}`} data-testid={`search-tab-${k}`}>
              <Icon size={14} /> {label} {q.trim().length >= 2 && !loading ? <span className="text-[10px] opacity-70">({n})</span> : null}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto" data-testid="search-results">
        {loading && <div className="flex justify-center py-8"><Spinner /></div>}
        {!loading && q.trim().length >= 2 && list.length === 0 && <Empty icon={SearchIcon} title="No matches" text={`Nothing found for “${q.trim()}”`} />}
        {!loading && q.trim().length < 2 && <Empty icon={SearchIcon} title="Type at least 2 characters" text="Searches your chats and the All Staff India feed." />}
        <ul className="divide-y divide-white/5">
          {!loading && tab === "messages" && list.map((m) => (
            <li key={m.id}>
              <Link to={m.link} className="flex gap-3 px-4 py-3 hover:bg-white/[0.03]" data-testid={`search-message-${m.id}`}>
                <Avatar name={m.with?.name} src={m.with?.avatar} size={38} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2"><div className="text-sm font-semibold text-slate-100 truncate">{m.with?.name}</div><div className="text-[11px] text-slate-500 shrink-0">{dayLabel(m.at)}</div></div>
                  <div className="text-xs text-slate-400 line-clamp-2"><span className="text-slate-500">{m.sender_name}: </span><Mark text={m.text} q={q.trim()} /></div>
                </div>
              </Link>
            </li>
          ))}
          {!loading && tab === "posts" && list.map((p) => (
            <li key={p.id}>
              <Link to={`/connect/group/post/${p.id}`} className="flex gap-3 px-4 py-3 hover:bg-white/[0.03]" data-testid={`search-post-${p.id}`}>
                <Avatar name={p.sender.name} src={p.sender.avatar} size={38} ceo={p.is_ceo} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2"><div className="text-sm font-semibold text-slate-100 truncate">{p.sender.name} <span className="text-slate-500 font-normal text-xs">· {p.is_ceo ? CEO_ORG : p.department}</span></div><div className="text-[11px] text-slate-500 shrink-0">{dayLabel(p.posted_at)}</div></div>
                  <div className="text-xs text-slate-400 line-clamp-2"><Mark text={p.text} q={q.trim()} /></div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
